import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Search, X, Crown, User, ShoppingBag, MapPin } from "lucide-react";

const SEGMENTS = {
    vip: { label: "VIP", color: "bg-wine text-cream" },
    regular: { label: "Regular", color: "bg-vineyard/15 text-vineyard" },
    new: { label: "Novo", color: "bg-gold-soft/20 text-gold-deep" },
    lead: { label: "Lead", color: "bg-wine-dark/5 text-wine-dark/60" },
};

export default function AdminCustomers() {
    const [list, setList] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);

    useEffect(() => { api.get("/admin/customers").then((r) => setList(r.data)); }, []);

    const filtered = list.filter((c) => {
        if (filter !== "all" && c.segment !== filter) return false;
        if (q && !`${c.name} ${c.email}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    const totals = {
        total: list.length,
        vip: list.filter((c) => c.segment === "vip").length,
        revenue: list.reduce((s, c) => s + (c.total_spent || 0), 0),
        avg_ltv: list.length ? list.reduce((s, c) => s + (c.total_spent || 0), 0) / list.length : 0,
    };

    return (
        <section className="space-y-6" data-testid="admin-customers">
            <div>
                <span className="label-overline text-wine/60">CRM</span>
                <h1 className="font-heading text-3xl text-wine-dark">Clientes</h1>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi label="Total" value={totals.total} icon={User} />
                <Kpi label="VIPs" value={totals.vip} icon={Crown} accent />
                <Kpi label="Receita total" value={`€${totals.revenue.toFixed(2)}`} icon={ShoppingBag} />
                <Kpi label="LTV médio" value={`€${totals.avg_ltv.toFixed(2)}`} icon={ShoppingBag} />
            </div>

            <div className="bg-white rounded-md border border-gold-soft/30 p-5">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div className="flex-1 min-w-[240px] relative">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-wine-dark/40" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Procurar nome ou email…" className="w-full pl-9 pr-3 py-2 bg-cream rounded-md text-sm focus:outline-none" data-testid="search-customers" />
                    </div>
                    <div className="flex gap-1 bg-cream rounded-md p-1">
                        {["all", "vip", "regular", "new", "lead"].map((f) => (
                            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider ${filter === f ? "bg-wine text-cream" : "text-wine-dark/60"}`} data-testid={`filter-${f}`}>
                                {f === "all" ? "Todos" : SEGMENTS[f]?.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left label-overline text-wine-dark/60 border-b border-wine-dark/10">
                                <th className="py-3 px-3">Cliente</th><th>Segmento</th><th>Encomendas</th><th>Gasto total</th><th>Última compra</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((c) => (
                                <tr key={c.id} onClick={() => setSelected(c)} className="border-t border-wine-dark/5 hover:bg-cream cursor-pointer" data-testid={`customer-row-${c.id}`}>
                                    <td className="py-3 px-3">
                                        <p className="font-semibold text-wine-dark">{c.name}</p>
                                        <p className="text-xs text-wine-dark/55">{c.email}</p>
                                    </td>
                                    <td>
                                        <span className={`label-overline px-2 py-1 rounded text-[10px] ${SEGMENTS[c.segment]?.color || "bg-wine-dark/5 text-wine-dark/60"}`}>
                                            {SEGMENTS[c.segment]?.label || c.segment}
                                        </span>
                                    </td>
                                    <td className="text-wine-dark/70">{c.orders_count}</td>
                                    <td className="font-heading text-base text-wine-dark">€{c.total_spent?.toFixed(2) || "0.00"}</td>
                                    <td className="text-xs text-wine-dark/55">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString("pt-PT") : "—"}</td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan="5" className="text-center py-10 text-wine-dark/60">Sem clientes.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && <CustomerDrawer id={selected.id} onClose={() => setSelected(null)} />}
        </section>
    );
}

function Kpi({ label, value, icon: Icon, accent }) {
    return (
        <div className={`p-5 rounded-md border shadow-sm ${accent ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex items-start justify-between">
                <span className={`label-overline ${accent ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                <Icon className={`h-4 w-4 ${accent ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />
            </div>
            <p className="font-heading text-3xl mt-2">{value}</p>
        </div>
    );
}

function CustomerDrawer({ id, onClose }) {
    const [data, setData] = useState(null);
    useEffect(() => { api.get(`/admin/customers/${id}`).then((r) => setData(r.data)); }, [id]);

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-elegant/40 backdrop-blur-sm" onClick={onClose} data-testid="customer-drawer">
            <div className="bg-cream w-full max-w-xl h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <span className="label-overline text-wine">Cliente</span>
                        <h2 className="font-heading text-3xl text-wine-dark mt-1">{data?.user?.name || "…"}</h2>
                        <p className="text-xs text-wine-dark/60 mt-1">{data?.user?.email}</p>
                    </div>
                    <button onClick={onClose} className="text-wine-dark/60 hover:text-wine"><X className="h-5 w-5" /></button>
                </div>

                {!data ? <p className="text-wine-dark/60">A carregar…</p> : (
                    <>
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            <Stat label="Encomendas" value={data.stats.orders_count} />
                            <Stat label="Gasto total" value={`€${data.stats.total_spent.toFixed(2)}`} />
                            <Stat label="Ticket médio" value={`€${data.stats.avg_order.toFixed(2)}`} />
                            <Stat label="Assinaturas ativas" value={data.stats.active_subs} />
                        </div>

                        <Block title="Histórico de encomendas">
                            {data.orders.length === 0 ? <p className="text-wine-dark/55 text-sm">Sem encomendas.</p> : data.orders.map((o) => (
                                <div key={o.id} className="flex items-center justify-between border-b border-wine-dark/5 last:border-0 py-2.5 text-sm">
                                    <div>
                                        <p className="font-mono text-xs text-wine-dark/70">#{o.id.slice(0, 8)}</p>
                                        <p className="text-xs text-wine-dark/55">{new Date(o.created_at).toLocaleDateString("pt-PT")} · {o.status}</p>
                                    </div>
                                    <span className="font-heading text-base text-wine-dark">€{o.total.toFixed(2)}</span>
                                </div>
                            ))}
                        </Block>

                        {data.addresses.length > 0 && (
                            <Block title="Moradas">
                                {data.addresses.map((a) => (
                                    <div key={a.id} className="flex items-start gap-2 py-2 text-sm border-b border-wine-dark/5 last:border-0">
                                        <MapPin className="h-4 w-4 text-wine/60 mt-0.5" strokeWidth={1.5} />
                                        <div>
                                            <p className="text-wine-dark">{a.full_name || a.label}</p>
                                            <p className="text-xs text-wine-dark/60">{a.street}, {a.postal_code} {a.city}</p>
                                        </div>
                                    </div>
                                ))}
                            </Block>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white border border-gold-soft/30 rounded-md p-4">
            <span className="label-overline text-wine/60">{label}</span>
            <p className="font-heading text-2xl text-wine-dark mt-1">{value}</p>
        </div>
    );
}

function Block({ title, children }) {
    return (
        <div className="mt-6 bg-white border border-gold-soft/30 rounded-md p-5">
            <h3 className="label-overline text-wine mb-3">{title}</h3>
            {children}
        </div>
    );
}
